import { RoutalTooltip } from '@nx-smartmonkey/ui';
import React from 'react';

import { Driver } from '../../../../domain/driver/Driver';
import { Stop } from '../../../../domain/stop/Stop';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { useRetrieveUserCredentials } from '../../../../hooks/user/useRetrieveUserCredentials';
import {
  BrokenConstraintsContent,
  hasDriverBrokenConstraints,
  hasStopBrokenConstraints,
} from './BrokenConstraintsTooltip';
import { hasAnyWarning, OptimizationWarningsContent } from './OptimizationWarningsTooltip';

type TooltipPlacementTypes =
  | `left`
  | `right`
  | `top`
  | `bottom`
  | `topLeft`
  | `topRight`
  | `bottomLeft`
  | `bottomRight`;

type ProblemsTooltipProps = {
  type: `driver` | `stop`;
  driver?: Driver;
  stop?: Stop;
  planDrivers?: Driver[];
  tooltipPlacement?: TooltipPlacementTypes;
};

const ProblemsTooltip = ({
  type,
  driver,
  stop,
  planDrivers,
  tooltipPlacement,
  children,
}: React.PropsWithChildren<ProblemsTooltipProps>) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: projectGeoFences } = useRetrieveActiveProjectGeoFences();

  if (type === `stop`) {
    if (!stop || !planDrivers) {
      return null;
    }

    const stopHasBrokenConstraints = hasStopBrokenConstraints({
      stop,
      driver,
      planGeoFences: projectGeoFences ?? [],
      activeProject,
      userCredentials,
    });

    const stopHasWarnings = hasAnyWarning({
      stop,
      planDrivers,
      planGeoFences: projectGeoFences ?? [],
      activeProject,
      userCredentials,
    });

    if (!stopHasBrokenConstraints && !stopHasWarnings) {
      return null;
    }
  }

  if (
    type === `driver` &&
    (!driver ||
      !hasDriverBrokenConstraints({ driver, planGeoFences: projectGeoFences ?? [], activeProject, userCredentials }))
  )
    return null;

  return (
    <RoutalTooltip
      id={`driver-${driver?.id}-problems`}
      placement={tooltipPlacement}
      title={
        <>
          <BrokenConstraintsContent type={type} driver={driver} stop={stop} />
          {stop && planDrivers ? <OptimizationWarningsContent stop={stop} planDrivers={planDrivers} /> : null}
        </>
      }
      overlayStyle={{
        maxWidth: `300px`,
      }}
    >
      {children}
    </RoutalTooltip>
  );
};

export default ProblemsTooltip;
